import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEA_CONTEXT } from '../lib/scoring';

const fmt = (n, d = 0) => n == null ? '—' : Number(n).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });

export default function Regional({ inputs }) {
  const { t } = useTranslation();
  const currRate    = inputs.currency_rate || 7.25;
  const chinaSpot   = inputs.pp_spot_qingdao || 10000;
  const chinaUSD    = chinaSpot / currRate;
  const seaLow      = inputs.sea_bopp_cfr_low || 1210;
  const seaHigh     = inputs.sea_bopp_cfr_high || 1370;
  const stockCostUSD = inputs.kaps_avg_cost != null ? inputs.kaps_avg_cost / currRate : null; // ?? not || (0 valid)
  const seaMid      = (seaLow + seaHigh) / 2;
  const advantage   = seaMid - chinaUSD;
  const stockAdv    = stockCostUSD != null ? seaMid - stockCostUSD : null;

  const PLAYERS = [
    { name: 'LSP Long Son (SCG Vietnam)', country: '🇻🇳 Vietnam',     status: 'SHUTTING MID-MAY',    statusColor: 'red',  detail: 'Maintenance + feedstock shortage from Hormuz. Ethane tie-in project. Removes significant Vietnam domestic PP supply May–June.', source: 'ICIS Apr 30' },
    { name: 'SCG Chemicals (SCGC)',       country: '🇹🇭 Thailand',    status: 'DAILY WAR ROOM',       statusColor: 'blu',  detail: 'Running daily crisis management for feedstock. ~20% global ethylene capacity non-operational per SCG. Studying JV with PTTGC to merge Map Ta Phut PP/PE.', source: 'ICIS Apr 29–30' },
    { name: 'PRefChem',                   country: '🇲🇾 Malaysia',    status: 'RESTARTING MAY',       statusColor: 'grn',  detail: 'No.2 PP plant (450K t/yr) restart May. No.1 intermittently running since Feb. Combined 900K t/yr — significant regional supply relief.', source: 'Polymerupdate' },
    { name: 'JG Summit',                  country: '🇵🇭 Philippines', status: 'OFFLINE',              statusColor: 'red',  detail: 'PP facilities offline. Philippines relies entirely on imports. Tightening allocations for other SEA countries. Chinese PP filling gap.', source: 'Polymerupdate' },
    { name: 'Indonesian importers',       country: '🇮🇩 Indonesia',   status: 'HIGH LOCAL STOCK',     statusColor: 'amb',  detail: 'Post-Eid stock-build. Anti-dumping investigation on PP imports ongoing. Import mandate withdrawn March. Chinese PP competitive at $40–50/MT freight.', source: 'Argus' },
    { name: 'Chinese exporters',          country: '🇨🇳 China',       status: 'RECORD EXPORTS',       statusColor: 'grn',  detail: 'PP exports record high March 2026, firm April. Vietnam + Indonesia dominant. Freight: $15–30/MT Vietnam/Thailand, $40–50/MT Indonesia. Acts as SEA price ceiling.', source: 'ICIS Apr 29' },
    { name: 'Middle East (Saudi/Iran/UAE)',country: '🌍 Middle East', status: 'EFFECTIVELY ABSENT',   statusColor: 'red',  detail: 'Hormuz closure eliminated Middle East PP from SEA. Pre-crisis: dominant exporter. Restoring requires Hormuz + 4–6 week backlog clearance.', source: 'Argus/ICIS' },
  ];

  const allBars = [
    stockCostUSD != null && { label: t('regional.bar_your_stock'), sub: t('regional.bar_your_stock_sub', { cost: fmt(inputs.kaps_avg_cost) }), usd: stockCostUSD, color: 'violet', tag: t('regional.bar_your_stock_tag') },
    { label: t('regional.bar_china'),    sub: t('regional.bar_china_sub'),    usd: chinaUSD, color: 'blue',   tag: t('regional.bar_china_tag') },
    { label: t('regional.bar_sea_pre'),  sub: t('regional.bar_sea_pre_sub'),  usd: 980,      color: 'green',  tag: t('regional.bar_sea_pre_tag') },
    { label: t('regional.bar_sea_mid'),  sub: t('regional.bar_sea_mid_sub'),  usd: 1110,     color: 'amber',  tag: t('regional.bar_sea_mid_tag') },
    { label: t('regional.bar_sea_low'),  sub: t('regional.bar_sea_low_sub', { val: fmt(seaLow) }),  usd: seaLow,  color: 'amber', tag: '' },
    { label: t('regional.bar_sea_high'), sub: t('regional.bar_sea_high_sub', { val: fmt(seaHigh) }), usd: seaHigh, color: 'red', tag: t('regional.bar_sea_high_tag') },
    { label: t('regional.bar_india'),    sub: t('regional.bar_india_sub'),    usd: 1325,     color: 'violet', tag: t('regional.bar_india_tag') },
  ].filter(Boolean);
  const bars = allBars;
  const maxUSD = Math.max(...bars.map(b => b.usd)) * 1.1;

  return (
    <div className="page-container">
      <div className="page-hdr">
        <h2>{t('regional.title')}</h2>
        <p>{t('regional.subtitle', { seaLow: fmt(seaLow), seaHigh: fmt(seaHigh), chinaUSD: fmt(chinaUSD, 0) })}</p>
      </div>

      {/* Key Metrics */}
      <div className="g4 mb-16">
        {[
          { label: t('regional.kpi_china_exworks'), val: `$${fmt(chinaUSD, 0)}/MT`, sub: t('regional.kpi_china_sub', { cny: fmt(chinaSpot), rate: currRate }), color: 'blue', tag: t('regional.kpi_china_tag'), badgeClass: 'blu' },
          { label: t('regional.kpi_sea_mid'), val: `$${fmt(seaMid, 0)}/MT`, sub: t('regional.kpi_sea_sub', { low: fmt(seaLow), high: fmt(seaHigh) }), color: 'amber', tag: t('regional.kpi_sea_tag'), badgeClass: 'amb' },
          { label: t('regional.kpi_cost_adv'), val: `$${fmt(advantage, 0)}/MT`, sub: t('regional.kpi_cost_adv_sub'), color: advantage > 0 ? 'green' : 'red', tag: advantage > 0 ? t('regional.kpi_cost_advantaged') : t('regional.kpi_cost_behind'), badgeClass: advantage > 0 ? 'grn' : 'red' },
          { label: t('regional.kpi_stock_adv'), val: stockAdv != null ? `$${fmt(stockAdv, 0)}/MT` : '—', sub: t('regional.kpi_stock_adv_sub', { cost: fmt(inputs.kaps_avg_cost) }), color: 'green', tag: t('regional.kpi_stock_tag'), badgeClass: 'grn' },
        ].map(k => (
          <div key={k.label} className="card" style={{ borderTop: `3px solid var(--${k.color})` }}>
            <div className="label-mono mb-4">{k.label}</div>
            <div style={{ fontFamily: 'var(--f)', fontSize: 24, fontWeight: 800, color: `var(--${k.color})`, letterSpacing: '-.03em' }}>{k.val}</div>
            <div style={{ fontSize: 11, color: 'var(--t2)', marginTop: 3 }}>{k.sub}</div>
            <div style={{ marginTop: 6 }}><span className={`badge badge-${k.badgeClass}`}>{k.tag}</span></div>
          </div>
        ))}
      </div>

      {/* Price Bar Chart */}
      <div className="card mb-16">
        <div className="card-title"><span className="card-title-text">{t('regional.price_chart_title')}</span></div>
        {bars.map(b => {
          // map color name to badge class
          const badgeMap = { green: 'grn', blue: 'blu', amber: 'amb', red: 'red', violet: 'pur' };
          const badgeClass = badgeMap[b.color] || 'neutral';
          return (
            <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 200, flex: '1 1 200px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t0)' }}>{b.label}</div>
                <div style={{ fontSize: 10, color: 'var(--t3)' }}>{b.sub}</div>
              </div>
              <div style={{ flex: '2 1 180px', position: 'relative', height: 10, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, height: 10, borderRadius: 3, background: `var(--${b.color})`, width: `${(b.usd / maxUSD) * 100}%`, opacity: .85, transition: 'width .5s' }} />
              </div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, minWidth: 90, textAlign: 'right', color: `var(--${b.color})` }}>
                ${fmt(b.usd, 0)}/MT
              </div>
              {b.tag && <span className={`badge badge-${badgeClass}`}>{b.tag}</span>}
            </div>
          );
        })}
      </div>

      {/* Strategic Insight — using dark theme tokens */}
      <div style={{
        background: 'linear-gradient(135deg, var(--green-l), var(--blue-l))',
        border: '1.5px solid var(--green-b)',
        borderRadius: 10,
        padding: '16px 18px',
        marginBottom: 16,
      }}>
        <div style={{
          fontFamily: 'var(--f)', fontSize: 14, fontWeight: 700,
          color: 'var(--green)', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {t('regional.strategic_title')}
        </div>
        <div className="g3" style={{ marginBottom: 12 }}>
          {[
            { label: t('regional.strategic_normal_adv'),     val: `$${SEA_CONTEXT.structural_premium_usd.low}–${SEA_CONTEXT.structural_premium_usd.high}/MT`, sub: t('regional.strategic_normal_sub'),    color: 'green' },
            { label: t('regional.strategic_pre_baseline'),   val: `$${SEA_CONTEXT.precrisis_bopp_cfr.low}–${SEA_CONTEXT.precrisis_bopp_cfr.high}/MT`,        sub: t('regional.strategic_pre_sub'),       color: 'green' },
            { label: t('regional.strategic_crisis_premium'), val: `+$${fmt(seaMid - SEA_CONTEXT.precrisis_bopp_cfr.high, 0)}/MT`,                            sub: t('regional.strategic_crisis_sub'),    color: 'red'   },
          ].map(m => (
            <div key={m.label} style={{
              background: 'var(--bg2)',
              borderRadius: 7,
              padding: '10px 12px',
              border: '1px solid var(--border)',
            }}>
              <div className="label-mono mb-4">{m.label}</div>
              <div style={{ fontFamily: 'var(--f)', fontSize: 16, fontWeight: 700, color: `var(--${m.color})`, letterSpacing: '-.02em' }}>{m.val}</div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 2 }}>{m.sub}</div>
            </div>
          ))}
        </div>
        <div style={{
          fontSize: 12,
          color: 'var(--t1)',
          lineHeight: 1.75,
          padding: '12px 14px',
          background: 'var(--bg2)',
          borderRadius: 7,
          border: '1px solid var(--green-b)',
        }}>
          <strong style={{ color: 'var(--t0)' }}>{t('regional.strategic_opportunity_label')}</strong>{' '}
          {t('regional.strategic_opportunity_body', { seaLow: fmt(seaLow), seaHigh: fmt(seaHigh) })}
          {' '}
          <strong style={{ color: 'var(--green)' }}>{t('regional.strategic_question')}</strong>
        </div>
      </div>

      {/* SEA Player Table */}
      <div className="card">
        <div className="card-title"><span className="card-title-text">{t('regional.players_title')}</span></div>
        <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 10 }}>
          {t('regional.players_intel_note')}
          <span style={{ display: 'block' }}>{t('regional.players_intel_note_zh')}</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('regional.th_player')}</th>
                <th>{t('regional.th_country')}</th>
                <th>{t('regional.th_status')}</th>
                <th>{t('regional.th_detail')}</th>
                <th>{t('regional.th_source')}</th>
              </tr>
            </thead>
            <tbody>
              {PLAYERS.map(p => (
                <tr key={p.name}>
                  <td style={{ fontWeight: 600, minWidth: 160, color: 'var(--t0)' }}>{p.name}</td>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--t1)' }}>{p.country}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <span className={`badge badge-${p.statusColor}`}>{p.status}</span>
                  </td>
                  <td style={{ fontSize: 11, color: 'var(--t1)', lineHeight: 1.5, minWidth: 280 }}>{p.detail}</td>
                  <td style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--t3)', whiteSpace: 'nowrap' }}>{p.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Freight context */}
      <div className="card" style={{ marginTop: 12 }}>
        <div className="card-title"><span className="card-title-text">{t('regional.freight_title')}</span></div>
        <div className="g3">
          {[
            { route: t('regional.freight_v_t'), rate: '$15–30/MT', note: t('regional.freight_v_t_note') },
            { route: t('regional.freight_id'),  rate: '$40–50/MT', note: t('regional.freight_id_note') },
            { route: t('regional.freight_ph'),  rate: '$35–45/MT', note: t('regional.freight_ph_note') },
          ].map(f => (
            <div key={f.route} style={{ padding: '12px 14px', background: 'var(--bg3)', borderRadius: 7, border: '1px solid var(--border)' }}>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--t3)', marginBottom: 5, letterSpacing: '.04em' }}>{f.route}</div>
              <div style={{ fontFamily: 'var(--f)', fontSize: 18, fontWeight: 700, color: 'var(--green)', letterSpacing: '-.02em' }}>{f.rate}</div>
              <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--t3)', marginTop: 3 }}>{t('regional.freight_source')}</div>
              <div style={{ fontSize: 11, color: 'var(--t1)', marginTop: 4, lineHeight: 1.5 }}>{f.note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
